import { useEffect, useState, Fragment } from "react";
import axios from "axios";
import { Loader2, Users, Phone, ChevronDown, ChevronUp, Truck, MapPin, CalendarDays, Clock, CheckCircle2, CircleDot } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return d;
  }
};

export const MoverRoster = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/movers/roster`).then(({ data }) => setData(data)).catch(() => setData({ movers: [] }));
  }, []);

  const toggle = (id) => setOpen((m) => ({ ...m, [id]: !m[id] }));

  if (data === null) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blaze" /></div>;
  }

  const movers = (data.movers || []).slice().sort((a, b) => {
    const ca = a.contractor_name || "", cb = b.contractor_name || "";
    if (ca !== cb) return ca.localeCompare(cb);
    return (b.total || 0) - (a.total || 0);
  });

  const contractors = [...new Set((data.movers || []).map((m) => m.contractor_name).filter(Boolean))].sort();
  const shown = filter === "all" ? movers : movers.filter((m) => m.contractor_name === filter);

  return (
    <div className="mt-6 pb-10" data-testid="mover-roster-tab">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-blaze">Jobs by Mover</p>
        <p className="mt-1 text-xs text-slate-500">Grouped by contractor. Each mover's new MoversNearby jobs and how payment was taken.</p>
      </div>

      <div className="mt-3">
        <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-500">Contractor</label>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full max-w-xs rounded-lg border border-line bg-slate-50 px-3 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-blaze sm:w-auto"
          data-testid="roster-contractor-filter"
        >
          <option value="all">All contractors</option>
          {contractors.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {shown.length === 0 ? (
        <p className="mt-8 border border-line/60 p-8 text-center text-sm text-slate-500" data-testid="roster-empty">No movers found.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {shown.map((m, i) => {
            const isOpen = !!open[m.id];
            const showHeader = i === 0 || shown[i - 1].contractor_name !== m.contractor_name;
            return (
              <Fragment key={m.id}>
              {showHeader && (
                <div className="flex items-center gap-2 pt-4 first:pt-0" data-testid="roster-contractor-group">
                  <Users className="h-4 w-4 text-blaze" />
                  <p className="font-display text-sm font-bold uppercase tracking-wider text-slate-900">{m.contractor_name}</p>
                </div>
              )}
              <div className="rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid={`roster-mover-${m.id}`}>
                <button
                  onClick={() => toggle(m.id)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
                  data-testid={`roster-toggle-${m.id}`}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50 font-display text-sm font-bold text-blaze">
                      {(m.name || "?").slice(0, 2).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <p className="font-display text-base font-bold text-slate-900">{m.name}</p>
                      <p className="truncate text-xs text-slate-500">
                        {m.phone || m.email}{m.pay_rate ? ` · $${m.pay_rate}/hr` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-600" data-testid={`roster-count-${m.id}`}>
                      {m.total} job{m.total === 1 ? "" : "s"}
                    </span>
                    {m.upcoming > 0 && (
                      <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-700">
                        {m.upcoming} upcoming
                      </span>
                    )}
                    {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-slate-100 px-4 py-3" data-testid={`roster-jobs-${m.id}`}>
                    {m.jobs.length === 0 ? (
                      <p className="py-3 text-center text-xs text-slate-400">No jobs assigned yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {m.jobs.map((j) => (
                          <div key={j.job_id} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid={`roster-job-${j.job_id}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-blaze">{j.job_id}</p>
                                <p className="text-xs font-semibold text-slate-800">{j.first_name} {j.last_name}</p>
                              </div>
                              <span className={`flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${j.status === "completed" ? "border-green-300 bg-green-50 text-green-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}>
                                {j.status === "completed" ? <CheckCircle2 className="h-3 w-3" /> : <CircleDot className="h-3 w-3" />}
                                {j.status === "completed" ? "Completed" : "Upcoming"}
                              </span>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-slate-600">
                              <span className="rounded-lg bg-white px-2 py-1"><CalendarDays className="mr-1 inline h-3 w-3 text-blaze" />{fmtDate(j.moving_date)}</span>
                              {j.start_time && <span className="rounded-lg bg-white px-2 py-1"><Clock className="mr-1 inline h-3 w-3 text-blaze" />{j.start_time}</span>}
                              {j.total_hours ? <span className="rounded-lg bg-white px-2 py-1">{j.total_hours}h</span> : null}
                              {j.truck && <span className="rounded-lg bg-white px-2 py-1"><Truck className="mr-1 inline h-3 w-3 text-blaze" />{j.truck}</span>}
                              {(m.jobs.length && (j.assigned_movers || []).length > 1) ? (
                                <span className="rounded-lg bg-white px-2 py-1"><Users className="mr-1 inline h-3 w-3 text-blaze" />with {j.assigned_movers.filter((n) => n !== m.name).join(", ") || "team"}</span>
                              ) : null}
                            </div>
                            <p className="mt-1.5 text-[11px] text-slate-500">
                              <MapPin className="mr-1 inline h-3 w-3 text-green-600" />{j.pickup_address || j.pickup_suburb || "—"}
                              <span className="mx-1 text-slate-300">→</span>
                              <MapPin className="mr-1 inline h-3 w-3 text-red-500" />{j.drop_address || j.drop_suburb || "—"}
                            </p>
                            <div className="mt-2 flex items-center gap-1.5 border-t border-slate-200/70 pt-2 text-[11px]" data-testid={`roster-payment-${j.job_id}`}>
                              {j.payment?.paid ? (
                                <span className="rounded-full border border-green-300 bg-green-50 px-2 py-1 font-bold text-green-700">
                                  PAID{j.payment.amount ? ` · $${j.payment.amount.toLocaleString()}` : ""}{j.payment.method ? ` · ${j.payment.method}` : ""}
                                </span>
                              ) : (
                                <span className="rounded-full border border-red-200 bg-red-50 px-2 py-1 font-bold text-red-600">UNPAID</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};
